import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, PhoneCall, AlertTriangle, Award, RefreshCw } from 'lucide-react';

// Self-view for the 'agent' role: only ever shows their own call scores and history.
// Both /reports/dashboard and /reports/assessments already restrict to the
// logged-in agent's own data server-side (matched via email -> agent profile).
const AgentScorecard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, assessRes] = await Promise.all([
        axios.get(`${API}/reports/dashboard`),
        axios.get(`${API}/reports/assessments?page_size=50`),
      ]);
      setDashboard(dashRes.data);
      setRows(assessRes.data.rows || []);
    } catch (error) {
      console.error('Failed to load scorecard', error);
    } finally {
      setLoading(false);
    }
  };

  const scoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-rose-600';
  };

  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="agent-scorecard">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Welcome, {user?.name}
          </h1>
          <p className="text-slate-600">Your personal call quality scorecard</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-slate-400" size={24} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-5 border border-indigo-200 bg-indigo-50">
                <PhoneCall size={22} className="text-indigo-600 mb-2" />
                <p className="text-2xl font-bold text-indigo-700" data-testid="scorecard-total-calls">{dashboard?.total_calls ?? 0}</p>
                <p className="text-xs text-indigo-600">My Calls</p>
              </Card>
              <Card className="p-5 border border-emerald-200 bg-emerald-50">
                <Award size={22} className="text-emerald-600 mb-2" />
                <p className="text-2xl font-bold text-emerald-700" data-testid="scorecard-avg-score">{dashboard?.avg_score ?? 0}%</p>
                <p className="text-xs text-emerald-600">Average Score</p>
              </Card>
              <Card className="p-5 border border-blue-200 bg-blue-50">
                <TrendingUp size={22} className="text-blue-600 mb-2" />
                <p className="text-2xl font-bold text-blue-700" data-testid="scorecard-pass-rate">{dashboard?.pass_rate ?? 0}%</p>
                <p className="text-xs text-blue-600">Pass Rate</p>
              </Card>
              <Card className="p-5 border border-rose-200 bg-rose-50">
                <AlertTriangle size={22} className="text-rose-600 mb-2" />
                <p className="text-2xl font-bold text-rose-700" data-testid="scorecard-violations">{dashboard?.total_violations ?? 0}</p>
                <p className="text-xs text-rose-600">Violations Flagged</p>
              </Card>
            </div>

            <Card className="p-6 border border-slate-200 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                My Audit History
              </h2>
              {rows.length > 0 ? (
                <div className="space-y-3">
                  {rows.map((r) => (
                    <div
                      key={r.assessment_id}
                      className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`scorecard-row-${r.assessment_id}`}
                    >
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{r.call_title}</p>
                        <p className="text-sm text-slate-600">
                          {r.sop_title} &middot; {new Date(r.assessed_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`text-lg font-bold ${scoreColor(r.overall_score)}`}>{r.overall_score}%</span>
                        <Button size="sm" variant="outline" onClick={() => navigate(`/assessment/${r.call_id}`)} data-testid={`scorecard-view-${r.call_id}`}>
                          View Details
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <PhoneCall size={48} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-600">No audited calls yet</p>
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AgentScorecard;
